import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';


const HeroSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentStat, setCurrentStat] = useState(0);
  const [realStats, setRealStats] = useState({ transfers: 0, players: 0 });
  const isRTL = i18n?.language === 'ar';

  useEffect(() => { fetchRealStats(); }, []);

  const fetchRealStats = async () => {
    try {
      const { count: playersCount } = await supabase?.from('players')?.select('*', { count: 'exact', head: true })?.eq('verified_status', true);
      const { count: transfersCount } = await supabase?.from('club_interest')?.select('*', { count: 'exact', head: true });
      setRealStats({ transfers: transfersCount || 0, players: playersCount || 0 });
    } catch (error) {
      console.error('Error fetching real stats:', error);
    }
  };

  const stats = [
    { value: `${realStats?.players}`, label: t('homepage.hero.verifiedPlayers'), icon: 'Users' },
    { value: `${realStats?.transfers}`, label: t('homepage.hero.transferInquiries'), icon: 'TrendingUp' },
    { value: '100%', label: t('homepage.hero.authenticData'), icon: 'Shield' },
    { value: 'FIFA', label: t('homepage.hero.compliantPlatform'), icon: 'Award' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats?.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stats?.length]);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden" style={{ background: 'linear-gradient(135deg, #060810 0%, #0a1428 35%, #081a10 65%, #060810 100%)' }}>
      {/* Animated football field lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full" style={{ border: '1px solid rgba(255,255,255,0.04)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] rounded-full" style={{ border: '1px solid rgba(255,255,255,0.03)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] rounded-full" style={{ border: '1px solid rgba(255,255,255,0.05)' }} />
        <div className="absolute top-0 bottom-0 left-1/2 w-px" style={{ background: 'rgba(255,255,255,0.03)' }} />
        <div className="absolute left-0 right-0 top-1/2 h-px" style={{ background: 'rgba(255,255,255,0.03)' }} />
        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-44 h-60" style={{ border: '1px solid rgba(255,255,255,0.03)', borderLeft: 'none' }} />
        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-44 h-60" style={{ border: '1px solid rgba(255,255,255,0.03)', borderRight: 'none' }} />
        <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 70px, rgba(255,255,255,0.012) 70px, rgba(255,255,255,0.012) 71px)' }} />
      </div>
      {/* Glowing orbs */}
      <div className="absolute top-20 left-10 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,100,255,0.12) 0%, transparent 70%)' }} />
      <div className="absolute bottom-20 right-10 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,180,80,0.1) 0%, transparent 70%)' }} />
      <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,107,0,0.08) 0%, transparent 70%)' }} />
      {/* Adidas ball watermark */}
      <div className="absolute top-12 right-12 opacity-[0.04] hidden xl:block pointer-events-none">
        <svg viewBox="0 0 200 200" width="320" height="320" fill="white">
          <circle cx="100" cy="100" r="95" fill="none" stroke="white" strokeWidth="4"/>
          <polygon points="100,8 135,45 178,45 150,78 162,122 100,98 38,122 50,78 22,45 65,45" fill="white" opacity="0.7"/>
          <circle cx="100" cy="100" r="18" fill="white"/>
          <circle cx="100" cy="100" r="95" fill="none" stroke="white" strokeWidth="2" strokeDasharray="8 4"/>
        </svg>
      </div>
      {/* Marketing Partners Top Bar */}
      <div className="relative z-20 w-full" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: 'rgba(255,255,255,0.4)' }}>Official Marketing Partners</span>
          </div>
          <div className="flex items-center gap-5">
            {/* EA Sports FC mini */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.35)' }}>
              <svg viewBox="0 0 90 30" width="72" height="24">
                <text x="0" y="12" fontFamily="Arial Black, sans-serif" fontSize="8" fontWeight="900" fill="#FF6B00" letterSpacing="1">EA SPORTS</text>
                <text x="0" y="28" fontFamily="Arial Black, sans-serif" fontSize="18" fontWeight="900" fill="white">FC</text>
                <rect x="24" y="17" width="66" height="2" fill="#FF6B00"/>
                <text x="26" y="27" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="700" fill="#FF6B00" letterSpacing="2">24/25</text>
              </svg>
            </div>
            <div className="w-px h-6" style={{ background: 'rgba(255,255,255,0.15)' }} />
            {/* PS5 mini */}
            <div className="flex items-center px-2.5 py-1 rounded-lg" style={{ background: 'rgba(0,52,143,0.2)', border: '1px solid rgba(0,100,255,0.35)' }}>
              <svg viewBox="0 0 55 24" width="44" height="19">
                <text x="0" y="20" fontFamily="Arial Black, sans-serif" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">PS5</text>
              </svg>
            </div>
          </div>
        </div>
      </div>
      {/* Main Hero Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-start space-y-6 md:space-y-8">
              {/* Premium Badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full" style={{ background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.4)', backdropFilter: 'blur(10px)' }}>
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                <Icon name="Award" size={16} color="#FF6B00" />
                <span className="text-sm font-bold tracking-wide" style={{ color: '#FF9500' }}>{t('homepage.hero.badge')}</span>
              </div>

              {/* Main Heading */}
              <div>
                <h1 className="text-5xl md:text-6xl lg:text-8xl font-black text-white leading-none tracking-tight mb-3">
                  <span style={{ background: 'linear-gradient(135deg, #ffffff 0%, #b3d4ff 40%, #80ffb3 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    {t('homepage.hero.title')}
                  </span>
                </h1>
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="h-1 w-20 rounded-full" style={{ background: 'linear-gradient(90deg, #FF6B00, #FF9500)' }} />
                  <div className="h-1 w-10 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }} />
                  <div className="h-1 w-5 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
                </div>
              </div>

              {/* Subtitle */}
              <p className="text-lg md:text-xl leading-relaxed font-light" style={{ color: 'rgba(180,210,255,0.8)' }}>
                {t('homepage.hero.subtitle')}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2">
                <button
                  onClick={() => navigate('/player-profiles')}
                  className="group relative overflow-hidden px-8 py-4 rounded-xl font-bold text-white text-base transition-all duration-300 hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #FF6B00 0%, #FF9500 100%)', boxShadow: '0 0 40px rgba(255,107,0,0.4), 0 4px 20px rgba(255,107,0,0.3)' }}
                >
                  <span className="relative z-10 flex items-center gap-2 justify-center">
                    <Icon name="Users" size={18} color="white" />
                    {t('homepage.hero.exploreTalent')}
                  </span>
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="px-8 py-4 rounded-xl font-bold text-white text-base transition-all duration-300 hover:scale-105"
                  style={{ border: '2px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.05)' }}
                >
                  <span className="flex items-center gap-2 justify-center">
                    <Icon name="Calendar" size={18} color="white" />
                    {t('homepage.hero.bookConsultation')}
                  </span>
                </button>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
                {stats?.map((stat, index) => (
                  <div
                    key={index}
                    className="relative p-4 rounded-xl transition-all duration-500"
                    style={{
                      background: currentStat === index
                        ? 'linear-gradient(135deg, rgba(255,107,0,0.2), rgba(255,149,0,0.1))'
                        : 'rgba(255,255,255,0.04)',
                      border: currentStat === index
                        ? '1px solid rgba(255,107,0,0.5)'
                        : '1px solid rgba(255,255,255,0.08)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: currentStat === index ? '0 0 20px rgba(255,107,0,0.2)' : 'none'
                    }}
                  >
                    <Icon name={stat?.icon} size={22} color={currentStat === index ? '#FF6B00' : 'rgba(255,255,255,0.5)'} className="mb-2" />
                    <div className="text-2xl font-black text-white number-ltr">{stat?.value}</div>
                    <div className="text-xs font-medium mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>{stat?.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Featured Player Card */}
            <div className="relative hidden lg:block">
              {/* PS5 Big Logo - Prominent Marketing Partner */}
              <div className="absolute -top-8 -right-4 z-20">
                <div className="flex flex-col items-center gap-1 px-5 py-3 rounded-2xl" style={{ background: 'rgba(0,20,80,0.85)', backdropFilter: 'blur(20px)', border: '2px solid rgba(0,100,255,0.5)', boxShadow: '0 0 40px rgba(0,80,255,0.3), 0 0 80px rgba(0,80,255,0.15)' }}>
                  <svg viewBox="0 0 200 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="160" height="64">
                    <text x="0" y="72" fontFamily="Arial Black, sans-serif" fontSize="80" fontWeight="900" fill="white" letterSpacing="-3">PS5</text>
                  </svg>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                    <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: 'rgba(150,200,255,0.9)' }}>PlayStation Partner</span>
                  </div>
                </div>
              </div>
              <div
                className="relative w-full aspect-[4/5] rounded-3xl overflow-hidden"
                style={{ boxShadow: '0 0 80px rgba(0,100,255,0.25), 0 0 160px rgba(0,180,80,0.1), 0 20px 60px rgba(0,0,0,0.6)' }}
              >
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1f4ab2585-1766847090960.png"
                  alt="Professional African football player in action on European stadium field, wearing blue jersey, mid-kick with intense focus and athletic form"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #060810 0%, rgba(6,8,16,0.3) 55%, transparent 100%)' }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,100,255,0.1) 0%, transparent 50%)' }} />

                {/* EA Sports Badge on card */}
                <div className="absolute top-4 left-4">
                  <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(15px)', border: '1px solid rgba(255,107,0,0.4)' }}>
                    <svg viewBox="0 0 90 30" width="72" height="24">
                      <text x="0" y="12" fontFamily="Arial Black, sans-serif" fontSize="8" fontWeight="900" fill="#FF6B00" letterSpacing="1">EA SPORTS</text>
                      <text x="0" y="28" fontFamily="Arial Black, sans-serif" fontSize="18" fontWeight="900" fill="white">FC</text>
                      <rect x="24" y="17" width="66" height="2" fill="#FF6B00"/>
                      <text x="26" y="27" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="700" fill="#FF6B00" letterSpacing="2">24/25</text>
                    </svg>
                  </div>
                </div>

                {/* Success Rate Badge */}
                <div className="absolute top-4 right-4 rounded-xl px-3 py-2.5" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(15px)', border: '1px solid rgba(0,120,255,0.4)' }}>
                  <div className="text-xs font-medium mb-0.5" style={{ color: 'rgba(150,190,255,0.8)' }}>Success Rate</div>
                  <div className="text-2xl font-black text-white">96%</div>
                </div>

                {/* Player Info Card */}
                <div className="absolute bottom-6 left-6 right-6 rounded-2xl p-5" style={{ background: 'rgba(6,8,16,0.9)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="flex items-start gap-4">
                    <div className="relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0" style={{ border: '2px solid rgba(255,107,0,0.5)' }}>
                      <Image
                        src="https://img.rocket.new/generatedImages/rocket_gen_img_1d29d4270-1763295849579.png"
                        alt="Professional headshot of African male football player with confident smile wearing red team jersey"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-black text-white mb-0.5">Kwame Mensah</h3>
                      <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>Midfielder &bull; Ghana &rarr; Premier League</p>
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg" style={{ background: 'linear-gradient(135deg, #00a651, #007a3d)' }}>
                        <Icon name="TrendingUp" size={13} color="white" />
                        <span className="text-xs font-bold text-white">&euro;8.5M Transfer &bull; 2025</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative glows */}
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(0,100,255,0.35)' }} />
              <div className="absolute -bottom-8 -left-8 w-48 h-48 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(0,180,80,0.25)' }} />
            </div>
          </div>
        </div>
      </div>
      {/* LARGE EA Sports FC + PS5 Marketing Partner Section */}
      <div className="relative z-10 w-full py-12" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.35em] mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>Official Gaming &amp; Technology Partners</p>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-32" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2))' }} />
              <Icon name="Gamepad2" size={16} color="rgba(255,255,255,0.3)" />
              <div className="h-px w-32" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.2), transparent)' }} />
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-20">
            {/* EA Sports FC - Large */}
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-2xl px-8 py-6" style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.25)' }}>
                <svg viewBox="0 0 340 110" fill="none" xmlns="http://www.w3.org/2000/svg" width="300" height="97">
                  <text x="0" y="38" fontFamily="Arial Black, sans-serif" fontSize="24" fontWeight="900" fill="#FF6B00" letterSpacing="4">EA SPORTS</text>
                  <text x="0" y="100" fontFamily="Arial Black, sans-serif" fontSize="72" fontWeight="900" fill="white" letterSpacing="3">FC</text>
                  <rect x="95" y="62" width="245" height="5" fill="#FF6B00"/>
                  <text x="100" y="95" fontFamily="Arial, sans-serif" fontSize="22" fontWeight="700" fill="#FF6B00" letterSpacing="5">24/25</text>
                </svg>
              </div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>Official Gaming Partner</p>
            </div>

            {/* Divider */}
            <div className="hidden md:flex flex-col items-center gap-3">
              <div className="w-px h-28" style={{ background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.25), transparent)' }} />
              <span className="text-2xl font-black" style={{ color: 'rgba(255,255,255,0.2)' }}>&times;</span>
              <div className="w-px h-28" style={{ background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.25), transparent)' }} />
            </div>

            {/* PS5 - Large */}
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-2xl px-8 py-6" style={{ background: 'rgba(0,52,143,0.15)', border: '1px solid rgba(0,100,255,0.25)' }}>
                <svg viewBox="0 0 340 110" fill="none" xmlns="http://www.w3.org/2000/svg" width="300" height="97">
                  <text x="0" y="100" fontFamily="Arial Black, sans-serif" fontSize="100" fontWeight="900" fill="white" letterSpacing="-3">PS5</text>
                  <rect x="240" y="12" width="3" height="90" fill="rgba(255,255,255,0.15)"/>
                  <text x="255" y="38" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" fill="rgba(255,255,255,0.6)" letterSpacing="3">PLAY</text>
                  <text x="255" y="58" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" fill="rgba(255,255,255,0.6)" letterSpacing="3">STATION</text>
                  <text x="255" y="78" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" fill="rgba(255,255,255,0.6)" letterSpacing="3">PARTNER</text>
                  <circle cx="320" cy="30" r="14" fill="none" stroke="#E8003D" strokeWidth="3"/>
                  <rect x="306" y="48" width="28" height="18" rx="3" fill="#003791" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
                  <polygon points="310,48 322,48 316,40" fill="#003791"/>
                </svg>
              </div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>Immersive Experience Partner</p>
            </div>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
            {[
              { icon: 'Zap', text: 'Haptic Feedback', color: '#FF6B00' },
              { icon: 'Volume2', text: '3D Audio', color: '#0078ff' },
              { icon: 'Gamepad2', text: 'EA FC 24/25', color: '#FF6B00' },
              { icon: 'Monitor', text: 'PS5 Exclusive', color: '#0078ff' },
              { icon: 'Star', text: 'Immersive Gameplay', color: '#FFB800' }
            ]?.map((feat, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Icon name={feat?.icon} size={14} color={feat?.color} />
                <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.6)' }}>{feat?.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-36 left-1/2 -translate-x-1/2 animate-bounce z-10">
        <div className="w-7 h-11 rounded-full flex items-start justify-center p-1.5" style={{ border: '2px solid rgba(255,255,255,0.25)' }}>
          <div className="w-1 h-2.5 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.5)' }} />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;